import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { BadgeCheck, ClipboardCheck, Plus, ScanLine, ThumbsUp, Wallet } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CompanyLogo } from "@/components/BrandMark";
import { useAuth } from "@/context/AuthContext";
import { Count, EmptyArt, RequestCard, Spinner, TaskCard, TextSheet, stagger, useMotion } from "@/components/work/WorkBits";
import { ActivityFeed } from "@/components/work/Activity";
import api, { apiError, setCompanyId } from "@/lib/api";
import { cn } from "@/lib/utils";

const OPS = {
  endorse: (x) => ["post", `/tasks/${x.id}/endorse`, {}, "Task endorsed"],
  note: (x, text) => ["post", `/tasks/${x.id}/note`, { text }, "Note sent to the MD"],
  in_progress: (x) => ["post", `/tasks/${x.id}/status`, { status: "in_progress" }, "Marked In progress"],
  done: (x) => ["post", `/tasks/${x.id}/status`, { status: "done" }, "Marked Done"],
  recommend: (x) => ["post", `/approvals/${x.id}/recommend`, { comment: "" }, "Request recommended"],
  return: (x, text) => ["post", `/approvals/${x.id}/return`, { text }, "Request returned"],
  approve: (x) => ["post", `/approvals/${x.id}/approve`, { comment: "" }, "Request approved"],
  reject: (x, text) => ["post", `/approvals/${x.id}/reject`, { text }, "Request rejected"],
};
const ASK = {
  note: { title: "Note to the MD", hint: "The task won't be forwarded until it's revised.", cta: "Send note" },
  return: { title: "Return to the applicant", hint: "Explain what needs to change.", cta: "Return" },
  reject: { title: "Reject request", hint: "Give the applicant a short reason.", cta: "Reject" },
};
const BUCKETS = [
  ["to_decide", "Decision pending", "request", "amber"],
  ["to_recommend", "To recommend", "request", "teal"],
  ["to_endorse", "To endorse", "task", "violet"],
  ["to_revise", "Noted · revise", "task", "rose"],
  ["to_progress", "Assigned to me", "task", "sky"],
];
const STATS = [["open", "Open"], ["in_progress", "In progress"], ["done", "Done"], ["in_review", "In review"], ["noted", "Noted"]];

const Section = ({ title, count, children, testid, id }) => (
  <section id={id} className="scroll-mt-24 space-y-3" data-testid={testid}>
    <div className="flex items-center gap-2"><h2 className="text-[15px] font-semibold text-slate-900">{title}</h2>{count !== undefined && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-600"><Count n={count} /></span>}</div>
    {children}
  </section>
);

const Grid = ({ items, render, cols = "md:grid-cols-2" }) => {
  const m = useMotion();
  return <div className={cn("grid gap-3", cols)}><AnimatePresence initial={false}>{items.map((x, i) => <motion.div key={x.id} layout {...stagger(m, i)}>{render(x)}</motion.div>)}</AnimatePresence></div>;
};

/** Slim greeting line; multi-company users get a small company filter in the page's own filter area. */
const Header = ({ d, company, setCompany }) => {
  const multi = d.companies.length > 1;
  return (
    <div className="flex flex-wrap items-end justify-between gap-3" data-testid="work-header">
      <h1 className="text-base font-semibold tracking-tight text-slate-900 sm:text-lg" data-testid="work-title">Tasks &amp; Approvals</h1>
      {multi && (
        <Select value={company} onValueChange={setCompany}>
          <SelectTrigger className="h-9 w-56 bg-white text-sm" data-testid="work-company-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All my companies</SelectItem>{d.companies.map((c) => <SelectItem key={c.id} value={c.id}><span className="flex items-center gap-2"><CompanyLogo company={c} className={c.has_logo ? "h-5 w-10" : "h-5 w-5 rounded-full text-[9px]"} testid={`work-company-logo-${c.id}`} />{c.name}</span></SelectItem>)}</SelectContent>
        </Select>
      )}
    </div>
  );
};

export default function WorkPage() {
  const [d, setD] = useState(null);
  const [company, setCompany] = useState("all");
  const [hidden, setHidden] = useState(new Set());
  const [ask, setAsk] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const m = useMotion();
  const loadRef = useRef();
  const load = useCallback(() => api.get("/work", { params: { company } }).then((r) => setD(r.data)).catch((e) => toast.error(apiError(e))), [company]);
  loadRef.current = load;
  useEffect(() => { load(); }, [load]);
  useEffect(() => { const f = () => loadRef.current(); window.addEventListener("task-updated", f); return () => window.removeEventListener("task-updated", f); }, []);

  const commit = (x, kind, text) => {
    const [method, path, body, label] = OPS[kind](x, text);
    const show = () => setHidden((h) => { const n = new Set(h); n.delete(x.id); return n; });
    setHidden((h) => new Set(h).add(x.id));
    const timer = setTimeout(() => {
      api[method](path, body, { headers: { "X-Company-Id": x.company_id } }).then(() => loadRef.current()).then(show).catch((e) => { show(); toast.error(apiError(e)); });
    }, 5000);
    toast(label, { duration: 5000, action: { label: "Undo", onClick: () => { clearTimeout(timer); show(); } } });
  };
  const act = (x, kind) => (ASK[kind] ? setAsk({ ...ASK[kind], x, kind }) : commit(x, kind));

  if (!d) return <Spinner />;
  const can = d.me.can;
  const multi = d.companies.length > 1 && (d.scope.length > 1 || company !== "all");
  const vis = (xs) => xs.filter((x) => !hidden.has(x.id));
  const attention = BUCKETS.flatMap(([k, tag, kind, tint]) => vis(d.attention[k]).map((x) => ({ ...x, _k: k, _tag: tag, _kind: kind, _tint: tint })));
  const count = (k) => vis(d.attention[k]).length;
  const awaiting = vis(d.awaiting).filter((t) => !d.attention.to_revise.some((x) => x.id === t.id));
  const assigned = d.assigned.filter((t) => !d.attention.to_progress.some((x) => x.id === t.id));
  const empty = !attention.length && !awaiting.length && !assigned.length && !d.my_requests.length;
  const jump = (id) => document.getElementById(id)?.scrollIntoView({ behavior: m.reduce ? "auto" : "smooth", block: "start" });
  const goCompany = (to) => { if (company !== "all") setCompanyId(company); navigate(to); };

  const tiles = [
    can.create && can.assign && { k: "new-task", icon: Plus, label: "New Task", tint: "indigo", go: () => goCompany("/tasks") },
    can.endorse && { k: "endorse", icon: ClipboardCheck, label: "To Endorse", tint: "violet", count: count("to_endorse"), go: () => jump("work-attention") },
    can.recommend && { k: "recommend", icon: ThumbsUp, label: "To Recommend", tint: "teal", count: count("to_recommend"), go: () => jump("work-attention") },
    (can.approve || d.me.admin || can.submit || can.recommend) && { k: "approvals", icon: BadgeCheck, label: "Approvals", tint: "amber", count: count("to_decide"), go: () => (count("to_decide") ? jump("work-attention") : goCompany("/approvals")) },
    can.submit && { k: "requests", icon: Wallet, label: "My Requests", tint: "pink", count: d.my_requests.filter((r) => r.status === "returned").length, go: () => (d.my_requests.length ? jump("work-my-requests") : goCompany("/approvals?view=mine")) },
    can.submit && { k: "scan", icon: ScanLine, label: "Scan Document", tint: "sky", go: () => goCompany("/approvals/new") },
  ].filter(Boolean);

  return (
    <div className="mx-auto max-w-[1200px] space-y-5 pb-4" data-testid="work-page">
      <Header d={d} company={company} setCompany={setCompany} />

      <div className="flex flex-wrap items-center gap-x-6 gap-y-2 border-y border-slate-200 py-3" data-testid="work-counts">
        {STATS.map(([k, l]) => (
          <span key={k} className="inline-flex items-baseline gap-1.5 text-sm text-slate-500" data-testid={`work-count-${k}`}>
            {l}<span className="text-base font-semibold tabular-nums text-slate-900"><Count n={d.counts.tasks[k]} /></span>
          </span>
        ))}
      </div>

      {tiles.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="work-quick-actions">
          {tiles.map((t, i) => (
            <motion.button key={t.k} type="button" {...stagger(m, i)} onClick={t.go} data-testid={`work-quick-${t.k}`}
              className={cn("inline-flex min-h-[40px] items-center gap-2 rounded-lg border px-3 text-sm font-medium", t.k === "new-task" ? "border-teal-600 bg-teal-600 text-white hover:bg-teal-700" : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50")}>
              <t.icon className="h-4 w-4" strokeWidth={2.2} />{t.label}
              {t.count > 0 && <span className={cn("rounded-full px-1.5 text-xs font-semibold", t.k === "new-task" ? "bg-white/20" : "bg-teal-50 text-teal-700")}>{t.count}</span>}
            </motion.button>
          ))}
        </div>
      )}

      <div className="grid gap-8 lg:grid-cols-[minmax(0,1fr)_380px]">
        <div className="min-w-0 space-y-7">
          {empty && (
            <div className="rounded-xl border border-slate-200 bg-white px-6 py-8 text-center" data-testid="work-empty">
              <EmptyArt /><p className="mt-2 text-base font-semibold text-slate-800">All clear for now</p>
              <p className="mt-1 text-sm text-slate-600">Tasks assigned to you and requests you're part of will show up here.</p>
            </div>
          )}
          {attention.length > 0 && (
            <Section id="work-attention" title="Needs your attention" count={attention.length} testid="work-attention">
              <Grid items={attention} render={(x) => (x._kind === "task"
                ? <TaskCard t={x} act={act} multi={multi} chips={x._k === "to_endorse"} tint={x._tint} tag={x._tag} />
                : <RequestCard r={x} act={act} multi={multi} tint={x._tint} tag={x._tag} />)} />
            </Section>
          )}
          {awaiting.length > 0 && <Section title="Awaiting Division Head endorsement" count={awaiting.length} testid="work-awaiting"><Grid items={awaiting} render={(t) => <TaskCard t={t} multi={multi} chips tint="violet" />} /></Section>}
          {assigned.length > 0 && <Section title="My tasks" count={assigned.length} testid="work-assigned"><Grid items={assigned} render={(t) => <TaskCard t={t} multi={multi} tint={t.status === "done" ? "emerald" : "sky"} />} /></Section>}
          {d.my_requests.length > 0 && <Section id="work-my-requests" title="My requests" count={d.my_requests.length} testid="work-my-requests"><Grid items={d.my_requests} render={(r) => <RequestCard r={r} multi={multi} tint={r.status === "approved" ? "emerald" : r.status === "rejected" ? "rose" : r.status === "returned" ? "amber" : "teal"} />} /></Section>}
        </div>
        <aside className="min-w-0" data-testid="work-feed-aside">
          <ActivityFeed feed={d.feed} multi={d.companies.length > 1} canDelete={user?.is_super_admin} onDeleted={load} />
        </aside>
      </div>
      <TextSheet ask={ask} onClose={() => setAsk(null)} onSubmit={(text) => { commit(ask.x, ask.kind, text); setAsk(null); }} />
    </div>
  );
}

